// @ts-strict-ignore
import { faMinus, faPlus } from "@fortawesome/pro-regular-svg-icons";
import { Fragment, type ReactNode, useEffect, useRef, useState } from "react";

import { Accordion, Checkbox, NoStyleAccordion, Typography } from "@aviary";
import { FilterTitle } from "@shared/components/CatalogSearch/Filters/shared/FilterTitle/FilterTitle";
import { AnalyticsEvent } from "@shared/hooks/useAnalyticsLogger/AnalyticsEvent";
import { SearchApplyEntryPoint } from "@shared/hooks/useAnalyticsLogger/StoreAnalyticsEntryPoint";
import { useAnalyticsLogger } from "@shared/hooks/useAnalyticsLogger/useAnalyticsLogger";
import { useCatalogSearch } from "@shared/hooks/useCatalogSearch/useCatalogSearch";
import { useIsSpotlightCampaignEnabled } from "@shared/hooks/useIsSpotlightCampaignEnabled/useIsSpotlightCampaignEnabled";
import { useOptimizedMegaMenuTaxonomyTree } from "@shared/hooks/useOptimizedMegaMenuTaxonomyTree/useOptimizedMegaMenuTaxonomyTree";
import { useOptimizedTaxonomyTags } from "@shared/hooks/useOptimizedTaxonomyTags/useOptimizedTaxonomyTags";
import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import { catalogSearchResultTitle } from "@shared/reactiveVar/useCatalogSearchResultTitle.store";
import { useCatalogTypeVar } from "@shared/reactiveVar/useCatalogType/useCatalogType.store";
import { searchAnalyticsVar } from "@shared/reactiveVar/useSearchAnalytics/useSearchAnalytics.store";
import { useSearchSessionIdVar } from "@shared/reactiveVar/useSearchSessionId/useSearchSessionId.store";
import {
  setSelectedOptimizedMegaMenuItem,
  useSelectedOptimizedMegaMenuItem,
} from "@shared/reactiveVar/useSelectedOptimizedMegaMenuItem.store";
import type { Taxonomy } from "@shared/types/graphqlGenerated";

import * as styles from "./FullscriptCategory.styles";

interface TaxonomyWithMetaProps extends Taxonomy {
  isSelected: boolean;
  isDescendantsSelected: boolean;
}

interface SelectedTaxonomy {
  depth: Taxonomy["depth"];
  showSelectedSiblings: boolean;
}

interface Props {
  isBold: boolean;
}

const FullscriptCategory = ({ isBold }: Props) => {
  const { activeOptimizedTaxonomies, setActiveOptimizedTaxonomies } = useCatalogSearch();
  const selectedOptimizedMegaMenuItem = useSelectedOptimizedMegaMenuItem();
  const lastSelectedOptimizedMegaMenuItem = useRef(null);

  const [openAccordions, setOpenAccordions] = useState({});
  const logAnalyticsEvent = useAnalyticsLogger();
  const { isClinic } = useSharedGlobalConfig();
  const searchSessionId = useSearchSessionIdVar();
  const { type } = useCatalogTypeVar();
  const { isSpotlightCampaignEnabled } = useIsSpotlightCampaignEnabled();
  const entryPoint =
    type === "Wholesale"
      ? SearchApplyEntryPoint.WHOLESALE_CATALOG_PAGE
      : SearchApplyEntryPoint.CATALOG_PAGE;

  const {
    selectedTaxonomiesFromOptimizedMegaMenuItem,
    optimizedTaxonomySearchTags,
    uiTaxonomyTree,
  } = useOptimizedMegaMenuTaxonomyTree();

  const optimizedTaxonomyTags = useOptimizedTaxonomyTags({
    optimizedTaxonomySearchTags,
    activeOptimizedTaxonomies,
  });

  const activeOptimizedTaxonomiesCount = activeOptimizedTaxonomies?.length ?? 0;

  useEffect(() => {
    if (isSpotlightCampaignEnabled) {
      setSelectedOptimizedMegaMenuItem(activeOptimizedTaxonomies?.[0]?.name);
    }

    if (
      selectedOptimizedMegaMenuItem !== lastSelectedOptimizedMegaMenuItem.current ||
      activeOptimizedTaxonomiesCount === 0
    ) {
      lastSelectedOptimizedMegaMenuItem.current = selectedOptimizedMegaMenuItem;
      setOpenAccordions({});
    }
  }, [selectedOptimizedMegaMenuItem, activeOptimizedTaxonomies]);

  if (activeOptimizedTaxonomiesCount === 0) {
    return null;
  }

  const updateOpenAccordions = (id: Taxonomy["id"]) =>
    openAccordions[id] === undefined &&
    setOpenAccordions({
      ...openAccordions,
      [id]: true,
    });

  const filterOptimizedTaxonomies = activeOptimizedTaxonomies?.filter(
    tax => tax?.type === "filter"
  );

  const expandAccordionOnInit = (taxonomy: TaxonomyWithMetaProps) => {
    let isAnyChildrenChecked = false;

    if (taxonomy?.children?.length ?? 0 > 0) {
      isAnyChildrenChecked = taxonomy.children.some((child: Taxonomy) => {
        return filterOptimizedTaxonomies.some(tax => tax.id === child.id);
      });
    }

    const didUserOpenAccordion = openAccordions[taxonomy.id] === true;

    const isDefaultExpanded =
      taxonomy.isDescendantsSelected ||
      taxonomy.isSelected ||
      isAnyChildrenChecked ||
      didUserOpenAccordion;

    if (isDefaultExpanded) {
      updateOpenAccordions(taxonomy.id);
    }

    return isDefaultExpanded;
  };

  const onCheckboxClick = (taxonomy: TaxonomyWithMetaProps) => {
    const { resultCount } = searchAnalyticsVar();

    logAnalyticsEvent({
      eventName: isClinic
        ? AnalyticsEvent.STORE_SEARCH_APPLY_FILTER
        : AnalyticsEvent.PATIENT_SEARCH_APPLY_FILTER,
      payload: {
        entry_point: entryPoint,
        filter_type: "Category",
        search_version: 2,
        content: taxonomy.name,
        result_count: resultCount,
        ...(searchSessionId && { search_session_id: searchSessionId }),
      },
    });

    const isSelected =
      filterOptimizedTaxonomies.some(actOptTax => actOptTax.id === taxonomy.id) ?? false;

    const distanceFromTop = window?.scrollY;

    // Unchecking a taxonomy
    if (isSelected) {
      const searchResultTitle = catalogSearchResultTitle();
      if (filterOptimizedTaxonomies.length === 1 && searchResultTitle.type === "taxonomy") {
        const searchOptimizedTaxonomies = activeOptimizedTaxonomies.filter(
          tax => tax.type === "search"
        );

        /**
         * If there's no active taxonomy after unchecking,
         * the closest parent taxonomy unselectable by users
         * will be selected
         */
        setActiveOptimizedTaxonomies([
          {
            name: (searchResultTitle.title as Taxonomy).name,
            id: (searchResultTitle.title as Taxonomy).id,
            hideFromDisplay: true,
            type: "lock",
          },
          ...searchOptimizedTaxonomies,
        ]);
        requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));

        return;
      }

      const updatedFilterTaxonomies = activeOptimizedTaxonomies.filter(
        activeTaxonomy => activeTaxonomy.type === "filter" && activeTaxonomy.id !== taxonomy.id
      );

      const searchOptimizedTaxonomies = activeOptimizedTaxonomies.filter(
        activeTaxonomy => activeTaxonomy.type === "search"
      );

      setActiveOptimizedTaxonomies([...updatedFilterTaxonomies, ...searchOptimizedTaxonomies]);
      requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));

      return;
    }

    /**
     * When checking a taxonomy:
     * 1. The checked children's checkbox will be unchecked
     * 2. The checked parent's checkbox will be unchecked
     * 3. The checked closest parent taxonomy unselectable
     *    by users will be unchecked
     */
    const taxonomyIdsToUnselect = [];

    if (taxonomy?.children?.length ?? 0 > 0) {
      taxonomy.children.forEach(tax => taxonomyIdsToUnselect.push(tax.id));
    }

    taxonomyIdsToUnselect.push(taxonomy.parentId);
    taxonomyIdsToUnselect.push((catalogSearchResultTitle().title as Taxonomy)?.id);

    const updatedFilterTaxonomies = activeOptimizedTaxonomies.filter(
      tax => tax.type === "filter" && !taxonomyIdsToUnselect.includes(tax.id)
    );

    const searchTaxonomies = activeOptimizedTaxonomies.filter(tax => tax.type === "search");

    setActiveOptimizedTaxonomies([
      ...updatedFilterTaxonomies,
      {
        id: taxonomy.id,
        name: taxonomy.name,
        type: "filter",
      },
      ...searchTaxonomies,
    ]);
    requestAnimationFrame?.(() => window?.scrollTo(0, distanceFromTop));
  };

  const renderItem = (taxonomy: TaxonomyWithMetaProps) => {
    if (taxonomy.isDescendantsSelected && !taxonomy.isSelected) {
      return (
        <div key={taxonomy.id}>
          <Typography>{taxonomy.name}</Typography>
        </div>
      );
    }

    return (
      <div key={taxonomy.id}>
        <Checkbox
          id={taxonomy.id}
          checked={filterOptimizedTaxonomies.some(tax => tax.id === taxonomy.id)}
          onClick={() => onCheckboxClick(taxonomy)}
        >
          {taxonomy.name}
        </Checkbox>
      </div>
    );
  };

  /**
   *  The renderCategories function renders the taxonomy tree based on the
   *  meta properties
   *
   *  Conditions:
   *  1. L1 category (depth 0) of the selected nodes is not visible in the UI
   *
   *  2. L2 category (depth 1) of the selected nodes is visible in the UI
   *     with a special style emphasis
   *
   *  3. If the selected is a leaf node, it will be rendered with a checkbox
   *     and the selected siblings and the siblings' descendants will be visible
   *
   *  4. If the selected node has descendants, it will be rendered without a checkbox
   *     and only the selected descendants will be visible
   *
   *  5. The L1 categories of the unselected nodes and its descendants are not visible
   *     in the UI
   */
  const renderCategories = (taxonomies: TaxonomyWithMetaProps[]): ReactNode => {
    return taxonomies.map((taxonomy: TaxonomyWithMetaProps) => {
      // Depth 0 unselected taxonomy skipped
      if (taxonomy.depth === 0 && !taxonomy.isDescendantsSelected) {
        return null;
      }

      // Depth 0 selected taxonomy
      if (taxonomy.depth === 0) {
        return renderCategories(taxonomy.children as TaxonomyWithMetaProps[]);
      }

      // Depth 1 unselected taxonomy skipped
      if (taxonomy.depth === 1 && !taxonomy.isDescendantsSelected && !taxonomy.isSelected) {
        return null;
      }

      // Depth 1 selected taxonomy render
      if (taxonomy.depth === 1) {
        return (
          <Fragment key={taxonomy.id}>
            <div css={styles.categoryL1Selected}>
              <Typography>
                <b>{taxonomy.name}</b>
              </Typography>
            </div>
            <div>{renderCategories(taxonomy.children as TaxonomyWithMetaProps[])}</div>
          </Fragment>
        );
      }

      const isLeafNode = (taxonomy?.children?.length ?? 0) === 0;

      // Depth 2 and beyond selected taxonomy
      if (taxonomy.isSelected) {
        if (isLeafNode) {
          return (
            <div
              key={taxonomy.id}
              css={[
                taxonomy.depth === 2 && styles.categoryL2,
                taxonomy.depth === 3 && styles.categoryL3,
                taxonomy.depth === 4 && styles.categoryL4,
              ]}
            >
              {renderItem(taxonomy)}
            </div>
          );
        }

        return (
          <Fragment key={taxonomy.id}>
            <div
              css={[
                taxonomy.depth === 2 && styles.categoryL2,
                taxonomy.depth === 3 && styles.categoryL3,
                taxonomy.depth === 4 && styles.categoryL4,
              ]}
            >
              <Typography>{taxonomy.name}</Typography>
            </div>
            <div>{renderCategories(taxonomy.children as TaxonomyWithMetaProps[])}</div>
          </Fragment>
        );
      }

      // Depth 2 and beyond selected descendants
      if (taxonomy.isDescendantsSelected) {
        return (
          <Fragment key={taxonomy.id}>
            <div
              css={[
                taxonomy.depth === 2 && styles.categoryL2,
                taxonomy.depth === 3 && styles.categoryL3,
                taxonomy.depth === 4 && styles.categoryL4,
              ]}
            >
              <Typography>{taxonomy.name}</Typography>
            </div>
            {renderCategories(taxonomy.children as TaxonomyWithMetaProps[])}
          </Fragment>
        );
      }

      // Depth 2 and beyond unselected and unselected-descendants
      // from a selected ancestor/sibling (or selected ancestor sibling)
      const toShow: boolean[] = selectedTaxonomiesFromOptimizedMegaMenuItem.current.map(
        (selectedTaxonomy: SelectedTaxonomy) => {
          if (!selectedTaxonomy) {
            return false;
          }

          if (taxonomy.depth === selectedTaxonomy.depth) {
            return selectedTaxonomy.showSelectedSiblings;
          }

          if (taxonomy.depth > selectedTaxonomy.depth) {
            return true;
          }

          return false;
        }
      );

      const isAnyTrue = toShow.some((show: boolean) => show);

      if (isAnyTrue) {
        if (isLeafNode) {
          return (
            <div
              key={taxonomy.id}
              css={[
                taxonomy.depth === 2 && styles.categoryL2,
                taxonomy.depth === 3 && styles.categoryL3,
                taxonomy.depth === 4 && styles.categoryL4,
              ]}
            >
              {renderItem(taxonomy)}
            </div>
          );
        }

        const isDefaultExpanded = expandAccordionOnInit(taxonomy);

        return (
          <NoStyleAccordion
            size="small"
            key={taxonomy.id}
            isDefaultExpanded={isDefaultExpanded}
            triggerStyles={[
              taxonomy.depth === 2 && styles.categoryL2,
              taxonomy.depth === 3 && styles.categoryL3,
              taxonomy.depth === 4 && styles.categoryL4,
              styles.accordion,
            ]}
            expandedStyles={styles.accordionExpanded}
            onChange={isOpen => setOpenAccordions({ ...openAccordions, [taxonomy.id]: isOpen })}
            coreTriggerContent={renderItem(taxonomy)}
            secondaryTriggerContent={
              openAccordions[taxonomy.id] === true ? (
                <FontAwesomeIcon icon={faMinus} css={styles.icon} />
              ) : (
                <FontAwesomeIcon icon={faPlus} css={styles.icon} />
              )
            }
          >
            {renderCategories(taxonomy.children as TaxonomyWithMetaProps[])}
          </NoStyleAccordion>
        );
      }

      return null;
    });
  };

  return (
    <Accordion
      isDefaultExpanded
      coreTriggerContent={
        <FilterTitle
          title="Category"
          count={optimizedTaxonomyTags.length}
          isBadge
          isSecondaryWeight={isBold}
        />
      }
    >
      {renderCategories(uiTaxonomyTree)}
    </Accordion>
  );
};

export { FullscriptCategory, type TaxonomyWithMetaProps };
